import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a number with thousand separators
 * @param num - The number to format
 * @returns Formatted number string with thousand separators
 */
export function formatNumber(num: number): string {
  return num.toLocaleString()
}

/**
 * Map a language code (e.g. "es", "he", "en") to a BCP-47 locale tag.
 */
function resolveLocale(langCode?: string | null): string | undefined {
  if (!langCode) return undefined
  const lower = langCode.toLowerCase()
  if (lower === 'es') return 'es-ES'
  if (lower === 'he') return 'he-IL'
  if (lower === 'en') return 'en-US'
  return langCode
}

/**
 * Format an area given in square meters into a human-readable, locale-aware string.
 * When the area is large enough (>= 1,000,000 m² / 1 km²), it is rendered in km²
 * with a couple of decimals. Otherwise it is rendered in m² with up to 2 decimals.
 *
 * Examples (locale "es-ES"):
 *   formatArea(125)          => "125 m²"
 *   formatArea(76932303.94)  => "76,93 km²"
 */
export function formatArea(areaInSquareMeters: number, langCode?: string | null): string {
  if (!Number.isFinite(areaInSquareMeters) || areaInSquareMeters < 0) {
    return `0 m²`
  }
  const locale = resolveLocale(langCode)
  const ONE_SQ_KM = 1_000_000
  if (areaInSquareMeters >= ONE_SQ_KM) {
    const km2 = areaInSquareMeters / ONE_SQ_KM
    const formatted = km2.toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    return `${formatted} km²`
  }
  const formatted = areaInSquareMeters.toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
  return `${formatted} m²`
}

export function isHttpLink(value: unknown): value is string {
  if (typeof value !== 'string') return false
  const trimmed = value.trim().toLowerCase()
  return trimmed.startsWith('http')
}

export function extractHttpLinkTuple(cell: unknown): { label: string; href: string } | null {
  if (!Array.isArray(cell) || cell.length < 2) return null
  const [label, href] = cell
  if (typeof label !== 'string' || typeof href !== 'string') return null
  if (!isHttpLink(href)) return null
  return { label, href: href.trim() }
}

type TableLinkTextToken = { type?: string; text?: string }
type TableLinkLangConfig = { text?: TableLinkTextToken[] }
type TableLinkFieldConfig = { [lang: string]: TableLinkLangConfig | undefined }

type TableFieldLike = {
  field?: string
  field_type?: string
  field_name?: string
  lang_title_en?: string
  lang_title_es?: string
  lang_title_he?: string
  lang_description_en?: string
  lang_description_es?: string
  lang_description_he?: string
  link?: TableLinkFieldConfig
}

export function normalizeLangKey(currentLanguage?: string): string | null {
  const lang = (currentLanguage ?? '').trim().toLowerCase()
  if (!lang) return null
  if (lang.startsWith('en')) return 'en'
  if (lang.startsWith('es')) return 'es'
  if (lang.startsWith('he') || lang.startsWith('iw')) return 'he' // Hebrew: he or legacy iw
  // Fallback to first 2-letter code if present (e.g. "en-US" -> "en")
  return lang.length >= 2 ? lang.slice(0, 2) : null
}

/** Matches `crud._normalize_lang_for_layer_titles` for `/layer_response` and map-proxy. */
const MAP_LAYER_RESPONSE_LANGS = new Set(['en', 'es', 'he', 'de', 'fr'])

/**
 * Resolves `chosen_lang` for `/api/map-proxy?type=layer_response` (and the API `layer_response` route).
 * When the UI language is missing or unknown, Israel defaults to Hebrew; other countries default to Spanish.
 */
export function chosenLangForLayerResponse(
  country: string | undefined,
  currentLanguage: string | undefined
): string {
  const trimmed = (currentLanguage ?? '').trim().toLowerCase()
  if (MAP_LAYER_RESPONSE_LANGS.has(trimmed)) return trimmed
  const c = (country ?? '').trim().toLowerCase()
  return c === 'israel' ? 'he' : 'es'
}

/** Parses `cityId` from property/search state; invalid values must not be sent as `city=undefined`. */
export function parseCityIdForMapLayers(cityId: string | number | undefined | null): number | null {
  if (cityId === undefined || cityId === null || cityId === '') return null
  const n = typeof cityId === 'string' ? parseInt(cityId, 10) : Number(cityId)
  if (!Number.isFinite(n) || n <= 0) return null
  return n
}

/** API fields often use `<base>_en` / `<base>_es` / `<base>_he`, sometimes a bare `<base>` (legacy Spanish). */
export type ContentLang = 'en' | 'es' | 'he'

function toContentLang(currentLanguage?: string | null): ContentLang {
  const k = normalizeLangKey(currentLanguage ?? undefined) ?? 'en'
  if (k === 'en' || k === 'es' || k === 'he') return k
  return 'en'
}

/** Resolve order: preferred language, then English, then the remaining locale. */
export function contentLangOrder(currentLanguage?: string | null): ContentLang[] {
  const pref = toContentLang(currentLanguage)
  if (pref === 'en') return ['en', 'es', 'he']
  const rest = (['en', 'es', 'he'] as const).filter((l) => l !== pref && l !== 'en')
  return [pref, 'en', ...rest]
}

/**
 * Read a localized string from `obj.<base>_<lang>` with fallback to `obj[base]`.
 *
 * The bare `obj[base]` field is treated as already being in the preferred
 * locale: the platform `/details` API serves localized content according to
 * the `lang` query param (e.g. Spanish copy lands in `section_name`, while
 * English mirrors land in `section_name_en`). Falling back to `_en` before the
 * bare key would surface English titles whenever the API only populated the
 * bare Spanish/Hebrew value, which is the common case.
 *
 * Order: preferred locale (`<base>_<pref>` then bare `<base>`) → `en` → other locales.
 */
export function pickLocalizedField(
  obj: Record<string, unknown> | null | undefined,
  base: string,
  currentLanguage?: string | null
): string {
  if (!obj) return ''
  const order = contentLangOrder(currentLanguage)
  const preferred = order[0]
  const preferredKey = `${base}_${preferred}`
  const preferredValue = obj[preferredKey]
  if (typeof preferredValue === 'string' && preferredValue.trim()) {
    return preferredValue.trim()
  }
  const bare = obj[base]
  if (typeof bare === 'string' && bare.trim()) return bare.trim()
  for (const lang of order.slice(1)) {
    const key = `${base}_${lang}`
    const v = obj[key]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return ''
}

/** Stable label for section anchors / search (English first, then es, he, bare). */
export function stableSectionIdName(obj: Record<string, unknown> | null | undefined): string | undefined {
  if (!obj) return undefined
  for (const k of ['section_name_en', 'section_name_es', 'section_name_he', 'section_name'] as const) {
    const v = obj[k]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return undefined
}

/** Stable fragment for DOM ids derived from field titles. */
export function stableLangTitleSlugPart(obj: Record<string, unknown> | null | undefined): string | undefined {
  if (!obj) return undefined
  for (const k of ['lang_title_en', 'lang_title_es', 'lang_title_he'] as const) {
    const v = obj[k]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return undefined
}

/** Strip trailing `_en` / `_es` / `_he` from API column keys (e.g. `parcel_name_en` → `parcel_name`). */
export function stripTrailingLocaleSuffix(s: string): string {
  return s.replace(/_(en|es|he)$/i, '')
}

/** When we must show a raw key, drop the locale suffix and soften underscores (never show `_en` in the UI). */
export function humanizeRawColumnKey(raw: string): string {
  const t = raw.trim()
  if (!t) return t
  const base = stripTrailingLocaleSuffix(t)
  return base.replace(/_/g, ' ').replace(/\s+/g, ' ').trim()
}

function extractStaticText(linkConfig: TableLinkLangConfig | undefined): string | null {
  const tokens = linkConfig?.text
  if (!Array.isArray(tokens)) return null
  const token = tokens.find(t => t?.type === 'static' && typeof t.text === 'string' && t.text.trim())
  return token?.text?.trim() || null
}

export function pickLocalizedString(params: {
  currentLanguage?: string
  en?: string | null
  es?: string | null
  he?: string | null
  fallback?: string | null
}): string {
  const { currentLanguage, en, es, he, fallback } = params
  const key = normalizeLangKey(currentLanguage)
  const enSafe = (en ?? '').trim()
  const esSafe = (es ?? '').trim()
  const heSafe = (he ?? '').trim()
  const fallbackSafe = (fallback ?? '').trim()

  if (key === 'en' && enSafe) return enSafe
  if (key === 'es' && esSafe) return esSafe
  if (key === 'he' && heSafe) return heSafe

  // Prefer English as global fallback if present, then Spanish, Hebrew, then explicit fallback
  return enSafe || esSafe || heSafe || fallbackSafe
}

function resolveFieldTitle(field: TableFieldLike | undefined, currentLanguage?: string): string | null {
  if (!field) return null
  const title = pickLocalizedField(field as Record<string, unknown>, 'lang_title', currentLanguage)
  return title?.trim() ? title.trim() : null
}

export function resolveTableColumnLabel(params: {
  tableFields?: unknown
  columnName?: string
  columnIndex?: number
  currentLanguage?: string
}): string {
  const { tableFields, columnName, columnIndex, currentLanguage } = params
  const rawName = (columnName ?? '').trim()

  if (!Array.isArray(tableFields)) return rawName
  const fields = tableFields as TableFieldLike[]

  // 1) Prefer strong matches by explicit field or localized titles
  const matchByStrongName = rawName
    ? fields.find(f => {
        if (!f) return false
        return (
          f.field === rawName ||
          f.lang_title_en === rawName ||
          f.lang_title_es === rawName ||
          f.lang_title_he === rawName
        )
      })
    : undefined
  const resolvedFromStrongName = resolveFieldTitle(matchByStrongName, currentLanguage)
  if (resolvedFromStrongName) return resolvedFromStrongName

  // 2) Then try descriptions (less reliable, but still explicit)
  const matchByDescription = rawName
    ? fields.find(f => {
        if (!f) return false
        return (
          f.lang_description_en === rawName ||
          f.lang_description_es === rawName ||
          f.lang_description_he === rawName
        )
      })
    : undefined
  const resolvedFromDescription = resolveFieldTitle(matchByDescription, currentLanguage)
  if (resolvedFromDescription) return resolvedFromDescription

  // 3) Field name is often duplicated (generic), only use if unique
  const fieldNameMatches = rawName
    ? fields.filter(f => !!f && f.field_name === rawName)
    : []
  if (fieldNameMatches.length === 1) {
    const resolvedFromFieldName = resolveFieldTitle(fieldNameMatches[0], currentLanguage)
    if (resolvedFromFieldName) return resolvedFromFieldName
  }

  // 4) Fallback: try by index alignment (common in API payloads)
  if (typeof columnIndex === 'number' && columnIndex >= 0 && columnIndex < fields.length) {
    const matchByIndex = fields[columnIndex]
    const resolvedFromIndex = resolveFieldTitle(matchByIndex, currentLanguage)
    if (resolvedFromIndex) return resolvedFromIndex
  }

  // 5) Column key may be `field_id_en` while `fields[].field` is `field_id` (no suffix)
  if (rawName) {
    const baseKey = stripTrailingLocaleSuffix(rawName)
    if (baseKey !== rawName) {
      const byBase = fields.find(
        (f) =>
          !!f &&
          (f.field === baseKey || f.field_name === baseKey || f.field === rawName || f.field_name === rawName)
      )
      const resolved = resolveFieldTitle(byBase, currentLanguage)
      if (resolved) return resolved
    }
  }

  if (rawName && /_(en|es|he)$/i.test(rawName)) {
    return humanizeRawColumnKey(rawName)
  }
  return rawName
}

export function resolveTableLinkLabel(params: {
  tableFields?: unknown
  columnName?: string
  currentLanguage?: string
  fallbackLabel?: string
}): string {
  const { tableFields, columnName, currentLanguage, fallbackLabel } = params

  const safeFallback = (fallbackLabel ?? '').trim()
  if (!Array.isArray(tableFields) || !columnName) return safeFallback || 'Enlace'

  const baseCol = stripTrailingLocaleSuffix(columnName.trim())
  const match = (tableFields as TableFieldLike[]).find(f => {
    if (!f || f.field_type !== 'link') return false
    return (
      f.field === columnName ||
      f.field === baseCol ||
      f.field_name === columnName ||
      f.field_name === baseCol ||
      f.lang_title_en === columnName ||
      f.lang_title_es === columnName ||
      f.lang_title_he === columnName ||
      f.lang_description_en === columnName ||
      f.lang_description_es === columnName ||
      f.lang_description_he === columnName
    )
  })

  const link = match?.link
  for (const k of contentLangOrder(currentLanguage)) {
    const text = extractStaticText(link?.[k])
    if (text) return text
  }

  return safeFallback || 'Enlace'
}
